import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { useDropzone } from 'react-dropzone'
import axios, { CancelTokenSource } from 'axios'
import { toastr } from 'react-redux-toastr'
import { CircleAlert, CircleCheck, ImagePlus, LoaderCircle, Upload, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import * as Mutation from '../../../api/gql/mutation'
import { formatBytes } from '../../../components/file-manager/hooks/useUpload'
import { REGISTER_PHOTOS } from '../api'
import { describePhotosError, photosErrorKey } from '../lib/errors'
import type { PhotoUi } from '../lib/permissions'
import { mimeOf, photoFileProblem, runPool } from '../lib/upload'
import BatchMetadataFields, { BatchMetadata, EMPTY_BATCH_METADATA } from './BatchMetadataFields'

const ACCEPT = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', '.jpg', '.jpeg', '.png', '.heic', '.heif']
const POOL_LIMIT = 4
const REGISTER_CHUNK = 200

type EntryState = 'queued' | 'uploading' | 'uploaded' | 'done' | 'failed'

interface Entry {
  id: string
  file: File
  problem: string | null
  state: EntryState
  progress?: number
  error?: string
}

interface Props {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultFolderId: string | null
  ui: PhotoUi
  /** Files dropped onto the photo area before the dialog opened (Freelo #33206294). */
  initialFiles?: File[]
}

let entrySeq = 0

const toEntry = (file: File, rejected = false): Entry => ({
  id: `f${entrySeq++}`,
  file,
  problem: photoFileProblem(file.name, file.size, file.type) ?? (rejected ? 'Photos_upload_bad_type' : null),
  state: 'queued',
})

const PhotoUploadDialog: React.FC<Props> = ({ projectId, open, onOpenChange, defaultFolderId, ui, initialFiles }) => {
  const { t } = useTranslation()
  const [entries, setEntries] = useState<Entry[]>([])
  const [meta, setMeta] = useState<BatchMetadata>({ ...EMPTY_BATCH_METADATA, folderId: defaultFolderId })
  const [busy, setBusy] = useState(false)
  // One cancel source per in-flight PUT: a failed file never touches its siblings; all are cancelled only on unmount.
  const sourcesRef = useRef<CancelTokenSource[]>([])
  useEffect(() => () => sourcesRef.current.forEach((src) => src.cancel('Dialog closed')), [])
  const opts = { context: { handlesErrors: true } }
  const [createFile] = useMutation(Mutation.CREATE_FILE, opts)
  const [setFileUploaded] = useMutation(Mutation.SET_FILE_UPLOADED, opts)
  const [deleteFile] = useMutation(Mutation.DELETE_FILE, opts)
  const [registerPhotos] = useMutation(REGISTER_PHOTOS, { ...opts, refetchQueries: ['photos', 'photoFolders'] })

  useEffect(() => {
    if (!open) return
    setEntries((initialFiles ?? []).map((f) => toEntry(f)))
    setMeta({ ...EMPTY_BATCH_METADATA, folderId: defaultFolderId })
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const patch = (id: string, change: Partial<Entry>) => setEntries((cur) => cur.map((e) => (e.id === id ? { ...e, ...change } : e)))

  const onDrop = useCallback((accepted: File[], rejected: File[]) => {
    setEntries((cur) => cur.concat(accepted.map((f) => toEntry(f)), rejected.map((f) => toEntry(f, true))))
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ accept: ACCEPT, multiple: true, onDrop, disabled: busy })

  const uploadable = entries.filter((e) => !e.problem && (e.state === 'queued' || e.state === 'failed'))

  const safeDelete = async (ids: string[]) => {
    if (!ids.length) return
    try {
      await deleteFile({ variables: { ids } })
    } catch (e) {
      console.warn('Failed to clean up photo files', e)
    }
  }

  const uploadOne = async (entry: Entry): Promise<{ entryId: string; fileId: string } | null> => {
    const { file } = entry
    let fileId: string | null = null
    try {
      const mimetype = mimeOf(file.name, file.type) as string
      const { data } = await createFile({ variables: { input: { filename: file.name, size: file.size, mimetype, encoding: 'utf8', directory: 'photos' } } })
      fileId = data.createFile.file.id as string
      const signedUrl = data.createFile.uploadUrl as string
      patch(entry.id, { state: 'uploading', progress: 0, error: undefined })
      // The URL is signed for `mimetype`; an empty file.type (HEIC) would make the PUT send another type and fail.
      const body = file.type ? file : new File([file], file.name, { type: mimetype })
      const source = axios.CancelToken.source()
      sourcesRef.current.push(source)
      try {
        await axios.put(signedUrl, body, {
          headers: { 'Content-Type': mimetype },
          cancelToken: source.token,
          onUploadProgress: (ev) => patch(entry.id, { progress: ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0 }),
        })
      } finally {
        sourcesRef.current = sourcesRef.current.filter((x) => x !== source)
      }
      await setFileUploaded({ variables: { ids: [fileId] } })
      patch(entry.id, { state: 'uploaded' })
      return { entryId: entry.id, fileId }
    } catch (e: any) {
      if (fileId) await safeDelete([fileId])
      patch(entry.id, { state: 'failed', error: describePhotosError(photosErrorKey(e), t) })
      return null
    }
  }

  const start = async () => {
    setBusy(true)
    try {
      const results = await runPool(uploadable, POOL_LIMIT, uploadOne)
      const ok = results.filter((r): r is { entryId: string; fileId: string } => !!r)
      const input = {
        folderId: meta.folderId,
        buildingId: meta.buildingId,
        floorId: meta.floorId,
        trade: meta.trade,
        albumId: ui.canManageStructure ? meta.albumId : null,
        tagIds: meta.tagIds,
        description: meta.description.trim() || null,
        visibility: meta.visibility,
      }
      let registered = 0
      let registerError: string | null = null
      for (let i = 0; i < ok.length; i += REGISTER_CHUNK) {
        const chunk = ok.slice(i, i + REGISTER_CHUNK)
        if (registerError) {
          await safeDelete(chunk.map((c) => c.fileId))
          chunk.forEach((c) => patch(c.entryId, { state: 'failed', error: registerError as string }))
          continue
        }
        try {
          await registerPhotos({ variables: { projectId, fileIds: chunk.map((c) => c.fileId), input } })
          registered += chunk.length
          chunk.forEach((c) => patch(c.entryId, { state: 'done' }))
        } catch (e: any) {
          registerError = describePhotosError(photosErrorKey(e), t)
          await safeDelete(chunk.map((c) => c.fileId))
          chunk.forEach((c) => patch(c.entryId, { state: 'failed', error: registerError as string }))
        }
      }
      if (registered) toastr.success(t('Common_success'), t('Photos_upload_done', { count: registered }))
      if (registerError) toastr.error(t('Common_error'), registerError)
      if (registered && registered === uploadable.length) onOpenChange(false)
    } finally {
      setBusy(false)
    }
  }

  const stateIcon = (e: Entry) => {
    if (e.state === 'done') return <CircleCheck className="h-4 w-4 text-green-600" />
    if (e.problem || e.state === 'failed') return <CircleAlert className="h-4 w-4 text-destructive" />
    if (e.state === 'uploading' || (busy && e.state === 'queued')) return <LoaderCircle className="h-4 w-4 animate-spin text-muted-foreground" />
    return <ImagePlus className="h-4 w-4 text-muted-foreground" />
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!busy) onOpenChange(next) }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            {t('Photos_upload')}
          </DialogTitle>
        </DialogHeader>

        <div
          {...getRootProps()}
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed p-6 text-center text-sm text-muted-foreground transition-colors',
            isDragActive && 'border-primary bg-accent',
            busy && 'pointer-events-none opacity-50',
          )}
        >
          <input {...getInputProps()} />
          <ImagePlus className="h-6 w-6" />
          <span>{t('Photos_upload_drop')}</span>
          <span className="text-xs">{t('Photos_upload_hint')}</span>
        </div>

        {entries.length > 0 && (
          <ul className="max-h-56 space-y-1 overflow-y-auto">
            {entries.map((e) => {
              const progress = e.progress
              const message = e.problem ? t(e.problem) : e.error
              return (
                <li key={e.id} className="flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm">
                  {stateIcon(e)}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{e.file.name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">{formatBytes(e.file.size, 1)}</span>
                    </div>
                    {message && <p className="text-xs text-destructive">{message}</p>}
                    {e.state === 'uploading' && <Progress value={progress ?? 0} className="mt-1 h-1.5" />}
                  </div>
                  {!busy && e.state !== 'done' && (
                    <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" aria-label={t('Photos_upload_remove')} onClick={() => setEntries((cur) => cur.filter((x) => x.id !== e.id))}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </li>
              )
            })}
          </ul>
        )}

        <BatchMetadataFields projectId={projectId} value={meta} onChange={setMeta} ui={ui} mode="upload" />

        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>{t('Common_cancel')}</Button>
          <Button disabled={busy || !uploadable.length} onClick={start}>
            {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {t('Photos_upload')}{uploadable.length > 0 && ` (${uploadable.length})`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default PhotoUploadDialog
